// ink-component
import React, { ReactNode } from 'react';
import { dateFormat } from '@carta/pen';
import moment, { Moment } from 'moment';

import { isNull } from '../../helpers';
import getGlobalProps from '../../helpers/constants/global-props';
import getInternationalDate, { isIsoDate } from './helpers/getInternationalDate';
import { extractDateTimeInfo } from '../../helpers/datetime/utils/extractDateTime/extractDateTime';

export interface FormattedDateProps {
  /** Original Date value to be formatted */
  value?: Date | Moment | string;
  /** Optional custom error node */
  error?: ReactNode;
  /** List of possible date formats */
  format?:
    | 'short'
    | 'condensed'
    | 'condensedWithTime'
    | 'month'
    | 'monthCondensed'
    | 'longWithAt'
    | 'timeOnly'
    | 'long'
    | 'i18n'
    | 'i18n-short'
    | 'i18n-time';
  /** By default we guess which timezone the user is, you can overwrite this behavior by passing a timezone.
   *  A timezone has a format [from the TZ library](https://en.wikipedia.org/wiki/List_of_tz_database_time_zones).  */
  timezone?: string;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
}

const timedFormats = ['condensedWithTime', 'long', 'longWithAt', 'timeOnly', 'i18n', 'i18n-short', 'i18n-time'];
const warning = format =>
  `[Ink Warning] The '${format}' format requires an ISO-8601 'value' prop. Not using an ISO date may cause the wrong date to be displayed. You can read more about this warning here: https://ink.carta.com/components/FormattedDate/usage`;
// eslint-disable-next-line import/no-mutable-exports
export let warnedAboutISOformats = false;

const warnTimedFormat = ({ value, format, timezone }) => {
  if (timedFormats.includes(format) && !(timezone || isIsoDate(value)) && !warnedAboutISOformats) {
    console.warn(warning(format));
    warnedAboutISOformats = true;
  }

  return null;
};

const globalProps = getGlobalProps({});

type FormattedDateProperties = {
  value: NonNullable<FormattedDateProps['value']>;
  format: NonNullable<FormattedDateProps['format']>;
  timezone: FormattedDateProps['timezone'];
};

type DateFormatFormat = NonNullable<Parameters<typeof dateFormat>[1]>['format'];

const getFormattedDate = ({ value, format, timezone }: FormattedDateProperties) => {
  if (format && format.includes('i18n')) {
    return getInternationalDate({ value, format, timezone });
  }

  if (format === 'condensedWithTime') {
    const { formatted } = extractDateTimeInfo({ value, format, timezone, evaluationMode: 'date-time' });

    if (timezone) {
      return formatted.timeZone;
    }

    return formatted.local;
  }

  return dateFormat(value, { format: format as DateFormatFormat, timezone });
};

function isInvalid(value: FormattedDateProps['value']) {
  if (isNull(value) || value === undefined) {
    return false;
  }
  if (typeof value === 'string') {
    return new Date(value).toString() === 'Invalid Date';
  }
  if (value instanceof Date) {
    return value.toString() === 'Invalid Date';
  }
  if (moment.isMoment(value)) {
    return !value.isValid();
  }
  return false;
}

/**
 * @purpose Formats your dates with ease
 * @group Formatted
 * @categories formatter
 */
function FormattedDate(props: FormattedDateProps) {
  const { value, format = 'short', error, timezone } = props;

  warnTimedFormat({ value, format, timezone });

  return (
    <>
      {!value || isInvalid(value) ? (
        <>{error}</>
      ) : (
        <span {...globalProps} data-testid={props['data-testid']}>
          {/* at this point, value is a valid date */}
          {getFormattedDate({ value, format, timezone })}
        </span>
      )}
    </>
  );
}

FormattedDate.defaultProps = {
  value: undefined,
  error: undefined,
  format: 'short',
  timezone: undefined,
  'data-testid': undefined,
};

FormattedDate.displayName = 'FormattedDate';

export default FormattedDate;
